import { useParams, Link } from 'react-router-dom';
import ArticleCard from '@/components/base/ArticleCard';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { getArticleBySlug, getCategoryName, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
            <i className="ri-file-search-line text-2xl"></i>
          </div>
          <p className="mt-4 text-foreground-600">文章未找到</p>
          <Link
            to="/"
            className="mt-4 inline-block px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = getCategoryName(article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://yourdomain.com/article/${article.slug}`,
    inLanguage: 'zh-CN',
    articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
    publisher: {
      '@type': 'Organization',
      name: '高潮喷水完全指南',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaArticle) }}
      />
      <article className="pt-24 pb-16 md:pb-20 min-h-screen">
        <BreadcrumbNav
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <div className="w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-block text-xs font-medium px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4 cursor-pointer"
              >
                {categoryName}
              </Link>
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-500"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} variant="compact" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </>
  );
}